// MobileInbox — the merged, severity-tiered Inbox (SLICE #7c-1).
//
// Surfaces, top to bottom, in severity order (critical → high → low) plus a local sync tier:
//   1. APPROVALS QUEUE (critical) — research_items in_review + research_output_drafts awaiting review.
//      Reads go through useCachedQuery (cached + never-blank + stale hint). Approve/Reject are LIVE
//      optimistic-concurrency WRITES via mobile_approve_item (handled in ApprovalCard) — NOT queued;
//      the buttons disable offline.
//   2. DRIFT ALERTS (high) — research_drift_alerts (open/acknowledged), severity-sorted. Read-only.
//   3. NOTIFICATIONS (low) — own-row unread notifications. Read-only here (mark-read is a write → omit).
//   4. SYNC-PENDING CAPTURES (local) — offlineQueue.list() non-'done' items. Works fully offline;
//      "Retry now" calls drainCaptureQueue() (disabled offline). Local store, NOT useCachedQuery.
//
// Offline substrate: every server read uses useCachedQuery and its queryFn THROWS on { error } so the
// last-known-good cached envelope keeps serving while offline; each cached section shows the same stale
// affordance as MobileHome. Empty results are valid (0 in_review, 0 pending drafts today) → quiet empty
// states, never an error. RAG/severity is icon+text (never colour-only).
//
// No new deps. The mobile_approve_item RPC + the research_status enum filter are cast through `as any`
// (generated types are stale); no types are regenerated.

import { useCallback, useEffect, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
  AlertCircle,
  AlertTriangle,
  Bell,
  CheckCircle2,
  CloudOff,
  Info,
  Inbox as InboxIcon,
  RefreshCw,
  Sparkles,
  UploadCloud,
} from 'lucide-react';
import { useIsOnline } from '@/hooks/useIsOnline';
import { useCachedQuery } from '@/lib/offline/cachedQuery';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { ApprovalCard, type ApprovalKind } from '@/components/mobile/ApprovalCard';
import { SuggestionCard } from '@/components/mobile/SuggestionCard';
import { list as listQueue, type QueueItem } from '@/lib/native/offlineQueue';
import { drainCaptureQueue } from '@/lib/capture/drainCaptures';

// ---------------------------------------------------------------------------
// Types + fetchers (queryFns MUST throw on { error } so the cached placeholder serves offline)
// ---------------------------------------------------------------------------

interface ApprovalRow {
  id: string;
  kind: ApprovalKind;
  title: string;
  status: string;
  updatedAt: string;
}

async function fetchApprovals(): Promise<ApprovalRow[]> {
  // research_items awaiting review. The research_status enum is stale in generated types → `as any`.
  const itemsRes = await (supabase.from('research_items') as any)
    .select('id, title, status, updated_at')
    .eq('status', 'in_review')
    .order('updated_at', { ascending: true });
  if (itemsRes.error) throw itemsRes.error;

  // research_output_drafts awaiting review — anything not already sent/approved/rejected. No `title`
  // column exists; derive a readable label from output_kind (+ external_id).
  const draftsRes = await (supabase.from('research_output_drafts') as any)
    .select('id, output_kind, external_id, status, updated_at')
    .not('status', 'in', '(sent,approved,rejected)')
    .order('updated_at', { ascending: true });
  if (draftsRes.error) throw draftsRes.error;

  const items: ApprovalRow[] = ((itemsRes.data ?? []) as any[]).map((r) => ({
    id: String(r.id),
    kind: 'item' as const,
    title: (r.title as string) || 'Untitled item',
    status: String(r.status ?? 'in_review'),
    updatedAt: String(r.updated_at),
  }));

  const drafts: ApprovalRow[] = ((draftsRes.data ?? []) as any[]).map((r) => {
    const kindLabel = prettyKind(r.output_kind as string | null);
    const ext = r.external_id ? ` · ${r.external_id}` : '';
    return {
      id: String(r.id),
      kind: 'draft' as const,
      title: `${kindLabel}${ext}`,
      status: String(r.status ?? 'pending'),
      updatedAt: String(r.updated_at),
    };
  });

  // Items (critical research) before drafts; each already updated_at-ascending (oldest waiting first).
  return [...items, ...drafts];
}

// ---- Captures awaiting confirmation (machine-proposed writes; doc §4.5) ----
// Direct table read of research_capture_suggestions under can_read_research() RLS. The NEW table is
// stale in generated types → `as any`. queryFn THROWS on { error } so the cached placeholder serves
// offline. Only status='proposed', unexpired, newest-first — the same set the hub inbox awaits.
interface SuggestionRow {
  id: string;
  suggestionType: string;
  title: string | null;
  rationale: string | null;
  confidence: number | null;
  payload: Record<string, unknown>;
  updatedAt: string;
}

async function fetchSuggestions(): Promise<SuggestionRow[]> {
  const { data, error } = await (supabase as any)
    .from('research_capture_suggestions')
    .select('id, suggestion_type, title, rationale, confidence, payload, expires_at, updated_at, created_at')
    .eq('status', 'proposed')
    .order('created_at', { ascending: false })
    .limit(100);
  if (error) throw error;
  const nowMs = Date.now();
  return ((data ?? []) as any[])
    .filter((r) => {
      // Drop already-expired proposals (mirrors the pending-count RPC's expiry guard).
      if (!r.expires_at) return true;
      const t = new Date(r.expires_at as string).getTime();
      return Number.isNaN(t) || t > nowMs;
    })
    .map((r) => ({
      id: String(r.id),
      suggestionType: String(r.suggestion_type ?? ''),
      title: (r.title as string | null) ?? null,
      rationale: (r.rationale as string | null) ?? null,
      confidence:
        typeof r.confidence === 'number' && Number.isFinite(r.confidence)
          ? (r.confidence as number)
          : r.confidence != null && Number.isFinite(Number(r.confidence))
            ? Number(r.confidence)
            : null,
      payload: (r.payload as Record<string, unknown>) ?? {},
      updatedAt: String(r.updated_at),
    }));
}

interface DriftRow {
  id: string;
  severity: string;
  summary: string;
  alertKind: string | null;
  detectedAt: string | null;
}

async function fetchDrift(): Promise<DriftRow[]> {
  const { data, error } = await (supabase.from('research_drift_alerts') as any)
    .select('id, severity, summary, alert_kind, detected_at, status')
    .in('status', ['open', 'acknowledged'])
    .order('detected_at', { ascending: false });
  if (error) throw error;
  const rows: DriftRow[] = ((data ?? []) as any[]).map((r) => ({
    id: String(r.id),
    severity: String(r.severity ?? 'low').toLowerCase(),
    summary: (r.summary as string) || 'Drift detected',
    alertKind: (r.alert_kind as string | null) ?? null,
    detectedAt: (r.detected_at as string | null) ?? null,
  }));
  // High → medium → low so the most severe sit at the top of the tier.
  return rows.sort((a, b) => severityRank(b.severity) - severityRank(a.severity));
}

interface NotificationRow {
  id: string;
  title: string;
  body: string | null;
  linkUrl: string | null;
  category: string | null;
  createdAt: string | null;
}

async function fetchNotifications(): Promise<NotificationRow[]> {
  // Own-row RLS (user_id = auth.uid()); only unread (status pending / read_at IS NULL).
  const { data, error } = await (supabase.from('notifications') as any)
    .select('id, title, body, link_url, category, status, read_at, created_at')
    .eq('status', 'pending')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return ((data ?? []) as any[]).map((r) => ({
    id: String(r.id),
    title: (r.title as string) || 'Notification',
    body: (r.body as string | null) ?? null,
    linkUrl: (r.link_url as string | null) ?? null,
    category: (r.category as string | null) ?? null,
    createdAt: (r.created_at as string | null) ?? null,
  }));
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function severityRank(severity: string): number {
  switch (severity) {
    case 'critical':
      return 3;
    case 'high':
      return 2;
    case 'medium':
      return 1;
    default:
      return 0; // low / unknown
  }
}

// RAG by icon + text (never colour-only): severity → label + icon + tone class.
function severityMeta(severity: string): { label: string; Icon: typeof AlertTriangle; tone: string } {
  switch (severity) {
    case 'critical':
    case 'high':
      return { label: severity === 'critical' ? 'Critical' : 'High', Icon: AlertTriangle, tone: 'text-red-600' };
    case 'medium':
      return { label: 'Medium', Icon: AlertCircle, tone: 'text-amber-600' };
    default:
      return { label: 'Low', Icon: Info, tone: 'text-muted-foreground' };
  }
}

function prettyKind(kind: string | null): string {
  if (!kind) return 'Draft output';
  return kind
    .split(/[_\-\s]+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
}

// Readable label for an approval's raw status. Covers research_items.status
// (draft/in_review/published/rejected) and research_output_drafts.status
// (draft/queued/sent/approved/rejected/superseded); anything unmapped falls
// back to Title Case so a new status is still readable, never raw snake_case.
function statusLabel(status: string): string {
  switch (status) {
    case 'in_review':
      return 'In review';
    case 'draft':
      return 'Draft';
    case 'queued':
      return 'Queued';
    case 'sent':
      return 'Sent';
    case 'approved':
      return 'Approved';
    case 'published':
      return 'Published';
    case 'rejected':
      return 'Rejected';
    case 'superseded':
      return 'Superseded';
    default:
      return prettyKind(status);
  }
}

function relative(iso: string | null): string | null {
  if (!iso) return null;
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return null;
  }
}

function prettyState(state: QueueItem['state']): string {
  switch (state) {
    case 'recording':
      return 'Recording';
    case 'pending_upload':
      return 'Waiting to upload';
    case 'uploading':
      return 'Uploading';
    case 'finalizing':
      return 'Finalising';
    case 'failed':
      return 'Failed';
    default:
      return state;
  }
}

// Shared stale-hint label (mirrors MobileHome).
function staleLabel(cachedAt: string | null): string {
  if (!cachedAt) return 'Showing saved data';
  try {
    return `Saved ${formatDistanceToNow(new Date(cachedAt), { addSuffix: true })}`;
  } catch {
    return 'Showing saved data';
  }
}

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

export default function MobileInbox() {
  const online = useIsOnline();
  const { toast } = useToast();

  const approvalsQuery = useCachedQuery<ApprovalRow[]>({
    queryKey: ['mobile-inbox-approvals'],
    cacheKey: 'mobile.inbox.approvals',
    queryFn: fetchApprovals,
  });
  const driftQuery = useCachedQuery<DriftRow[]>({
    queryKey: ['mobile-inbox-drift'],
    cacheKey: 'mobile.inbox.drift',
    queryFn: fetchDrift,
  });
  const notificationsQuery = useCachedQuery<NotificationRow[]>({
    queryKey: ['mobile-inbox-notifications'],
    cacheKey: 'mobile.inbox.notifications',
    queryFn: fetchNotifications,
  });
  const suggestionsQuery = useCachedQuery<SuggestionRow[]>({
    queryKey: ['mobile-inbox-suggestions'],
    cacheKey: 'mobile.inbox.suggestions',
    queryFn: fetchSuggestions,
  });

  // Locally-resolved approval ids (optimistically dropped once the RPC lands) so the row disappears
  // without waiting for a refetch.
  const [resolvedIds, setResolvedIds] = useState<Set<string>>(() => new Set());
  const onApprovalResolved = useCallback((id: string) => {
    setResolvedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    // Pull a fresh queue in the background so the row is durably gone after the next round-trip.
    void approvalsQuery.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Locally-resolved suggestion ids (own set — a suggestion and an approval could share an id space
  // only by accident, but keeping them separate keeps each section's optimistic-drop clean).
  const [resolvedSuggestionIds, setResolvedSuggestionIds] = useState<Set<string>>(() => new Set());
  const onSuggestionResolved = useCallback((id: string) => {
    setResolvedSuggestionIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    void suggestionsQuery.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const approvals = (approvalsQuery.data ?? []).filter((a) => !resolvedIds.has(a.id));
  const drift = driftQuery.data ?? [];
  const notifications = notificationsQuery.data ?? [];
  const suggestions = (suggestionsQuery.data ?? []).filter(
    (s) => !resolvedSuggestionIds.has(s.id),
  );

  // ---- Sync-pending captures (local, works fully offline) ----
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [retrying, setRetrying] = useState(false);

  const refreshQueue = useCallback(async () => {
    try {
      const items = await listQueue();
      // Non-'done' only; newest-first by updatedAt.
      setQueue(
        items.filter((i) => i.state !== 'done').sort((a, b) => b.updatedAt - a.updatedAt),
      );
    } catch {
      setQueue([]);
    }
  }, []);

  useEffect(() => {
    void refreshQueue();
  }, [refreshQueue]);

  const onRetryCaptures = useCallback(async () => {
    if (retrying || !online) return;
    setRetrying(true);
    try {
      const count = await drainCaptureQueue();
      await refreshQueue();
      toast({
        title: count > 0 ? 'Retrying uploads' : 'Nothing to retry',
        description:
          count > 0
            ? `Re-driving ${count} capture${count === 1 ? '' : 's'}.`
            : 'No captures are waiting to upload right now.',
      });
    } finally {
      setRetrying(false);
    }
  }, [retrying, online, refreshQueue, toast]);

  // Stale affordances (cached read currently being served).
  const approvalsStale = approvalsQuery.isFromCache && approvalsQuery.cachedAt != null;
  const suggestionsStale = suggestionsQuery.isFromCache && suggestionsQuery.cachedAt != null;
  const driftStale = driftQuery.isFromCache && driftQuery.cachedAt != null;
  const notificationsStale = notificationsQuery.isFromCache && notificationsQuery.cachedAt != null;

  // Header stale hint: surface the first cached read so the user knows something is from saved data.
  const anyStale = approvalsStale || suggestionsStale || driftStale || notificationsStale;
  const headerStaleAt: string | null =
    (approvalsStale ? approvalsQuery.cachedAt : null) ??
    (suggestionsStale ? suggestionsQuery.cachedAt : null) ??
    (driftStale ? driftQuery.cachedAt : null) ??
    (notificationsStale ? notificationsQuery.cachedAt : null) ??
    null;

  return (
    <div className="min-h-screen bg-background px-4 pb-8 pt-[calc(1.25rem+env(safe-area-inset-top))]">
      <header className="mb-4">
        <h1 className="font-heading text-2xl font-semibold text-primary">Inbox</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Approvals, captures to confirm, drift alerts, notifications and sync-pending uploads — most
          urgent first.
        </p>
        {anyStale && (
          <p
            className={cn(
              'mt-1 text-[11px] font-medium uppercase tracking-wide',
              online ? 'text-muted-foreground/70' : 'text-amber-600',
            )}
          >
            {staleLabel(headerStaleAt)}
          </p>
        )}
      </header>

      <div className="space-y-6">
        {/* ---------- CRITICAL: Approvals queue ---------- */}
        <section aria-labelledby="inbox-approvals">
          <SectionHeading
            id="inbox-approvals"
            icon={<AlertTriangle className="h-4 w-4 text-red-600" aria-hidden="true" />}
            tier="Critical"
            title="Approvals"
            count={approvals.length}
            stale={approvalsStale ? staleLabel(approvalsQuery.cachedAt) : null}
            online={online}
          />
          {approvals.length === 0 ? (
            <EmptyState text="Nothing waiting on you." />
          ) : (
            <ul className="space-y-3">
              {approvals.map((a) => (
                <ApprovalCard
                  key={`${a.kind}:${a.id}`}
                  id={a.id}
                  kind={a.kind}
                  title={a.title}
                  status={statusLabel(a.status)}
                  updatedAt={a.updatedAt}
                  online={online}
                  onResolved={() => onApprovalResolved(a.id)}
                />
              ))}
            </ul>
          )}
        </section>

        {/* ---------- CRITICAL: Captures awaiting confirmation (machine-proposed writes) ---------- */}
        <section aria-labelledby="inbox-suggestions">
          <SectionHeading
            id="inbox-suggestions"
            icon={<Sparkles className="h-4 w-4 text-red-600" aria-hidden="true" />}
            tier="Critical"
            title="Captures awaiting confirmation"
            count={suggestions.length}
            stale={suggestionsStale ? staleLabel(suggestionsQuery.cachedAt) : null}
            online={online}
          />
          {suggestions.length === 0 ? (
            <EmptyState text="No captures waiting to confirm." />
          ) : (
            <ul className="space-y-3">
              {suggestions.map((s) => (
                <SuggestionCard
                  key={s.id}
                  id={s.id}
                  suggestionType={s.suggestionType}
                  title={s.title}
                  rationale={s.rationale}
                  confidence={s.confidence}
                  payload={s.payload}
                  updatedAt={s.updatedAt}
                  online={online}
                  onResolved={() => onSuggestionResolved(s.id)}
                />
              ))}
            </ul>
          )}
        </section>

        {/* ---------- HIGH: Drift alerts ---------- */}
        <section aria-labelledby="inbox-drift">
          <SectionHeading
            id="inbox-drift"
            icon={<AlertCircle className="h-4 w-4 text-amber-600" aria-hidden="true" />}
            tier="High"
            title="Drift alerts"
            count={drift.length}
            stale={driftStale ? staleLabel(driftQuery.cachedAt) : null}
            online={online}
          />
          {drift.length === 0 ? (
            <EmptyState text="No open drift alerts." />
          ) : (
            <ul className="space-y-3">
              {drift.map((d) => {
                const sev = severityMeta(d.severity);
                const when = relative(d.detectedAt);
                return (
                  <li key={d.id} className="rounded-xl border border-border bg-card p-4">
                    <div className="flex items-start gap-2">
                      <sev.Icon className={cn('mt-0.5 h-4 w-4 shrink-0', sev.tone)} aria-hidden="true" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm leading-snug">{d.summary}</p>
                        <p className="mt-1 text-[11px] uppercase tracking-wide text-muted-foreground/70">
                          <span className={cn('font-semibold', sev.tone)}>{sev.label}</span>
                          {d.alertKind ? ` · ${prettyKind(d.alertKind)}` : ''}
                          {when ? ` · ${when}` : ''}
                        </p>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* ---------- LOW: Notifications ---------- */}
        <section aria-labelledby="inbox-notifications">
          <SectionHeading
            id="inbox-notifications"
            icon={<Bell className="h-4 w-4 text-muted-foreground" aria-hidden="true" />}
            tier="Low"
            title="Notifications"
            count={notifications.length}
            stale={notificationsStale ? staleLabel(notificationsQuery.cachedAt) : null}
            online={online}
          />
          {notifications.length === 0 ? (
            <EmptyState text="You’re all caught up." />
          ) : (
            <ul className="space-y-3">
              {notifications.map((n) => {
                const when = relative(n.createdAt);
                return (
                  <li key={n.id} className="rounded-xl border border-border bg-card p-4">
                    <div className="flex items-start gap-2">
                      <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                      <div className="min-w-0 flex-1">
                        <p className="font-heading text-sm font-semibold leading-snug">{n.title}</p>
                        {n.body && <p className="mt-1 text-sm text-muted-foreground">{n.body}</p>}
                        <p className="mt-1 text-[11px] uppercase tracking-wide text-muted-foreground/70">
                          {n.category ? prettyKind(n.category) : 'Notification'}
                          {when ? ` · ${when}` : ''}
                        </p>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* ---------- LOCAL: Sync-pending captures (works offline) ---------- */}
        <section aria-labelledby="inbox-sync">
          <div className="mb-2 flex items-center justify-between gap-2">
            <SectionHeading
              id="inbox-sync"
              icon={<UploadCloud className="h-4 w-4 text-muted-foreground" aria-hidden="true" />}
              tier="On device"
              title="Sync-pending"
              count={queue.length}
              stale={null}
              online={online}
              inline
            />
            {queue.length > 0 && (
              <button
                type="button"
                onClick={onRetryCaptures}
                disabled={retrying || !online}
                className={cn(
                  'inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border bg-muted/40 px-3 py-1.5 text-xs font-medium transition-colors active:bg-muted',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                )}
                aria-label="Retry pending captures now"
              >
                <RefreshCw className={cn('h-3.5 w-3.5', retrying && 'animate-spin')} aria-hidden="true" />
                Retry now
              </button>
            )}
          </div>
          {queue.length === 0 ? (
            <EmptyState
              icon={<CheckCircle2 className="h-4 w-4 text-muted-foreground/60" aria-hidden="true" />}
              text="All captures are synced."
            />
          ) : (
            <>
              {!online && (
                <p className="mb-2 flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-amber-600">
                  <CloudOff className="h-3 w-3" aria-hidden="true" />
                  Offline — these upload when you reconnect
                </p>
              )}
              <ul className="space-y-3">
                {queue.map((q) => {
                  const failed = q.state === 'failed';
                  const when = relative(new Date(q.updatedAt).toISOString());
                  return (
                    <li key={q.id} className="rounded-xl border border-border bg-card p-4">
                      <div className="flex items-start gap-2">
                        {failed ? (
                          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden="true" />
                        ) : (
                          <UploadCloud className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-sm leading-snug">
                            {q.contentType?.startsWith('audio') ? 'Audio capture' : 'Capture'}
                          </p>
                          <p className="mt-1 text-[11px] uppercase tracking-wide text-muted-foreground/70">
                            <span className={cn('font-semibold', failed && 'text-red-600')}>
                              {prettyState(q.state)}
                            </span>
                            {typeof q.attempts === 'number' && q.attempts > 0
                              ? ` · ${q.attempts} attempt${q.attempts === 1 ? '' : 's'}`
                              : ''}
                            {when ? ` · ${when}` : ''}
                          </p>
                          {failed && q.lastError && (
                            <p className="mt-1 text-[11px] text-red-600/90">{q.lastError}</p>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------

interface SectionHeadingProps {
  id: string;
  icon: React.ReactNode;
  tier: string;
  title: string;
  count: number;
  stale: string | null;
  online: boolean;
  /** When inline (sync row), drop the bottom margin so the parent flex controls spacing. */
  inline?: boolean;
}

function SectionHeading({ id, icon, tier, title, count, stale, online, inline }: SectionHeadingProps) {
  return (
    <div className={cn(!inline && 'mb-2')}>
      <div className="flex items-center gap-2">
        {icon}
        <h2 id={id} className="font-heading text-sm font-semibold">
          {title}
        </h2>
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground/70">{tier}</span>
        {count > 0 && (
          <span className="ml-auto rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold tabular-nums text-muted-foreground">
            {count}
          </span>
        )}
      </div>
      {stale && (
        <p
          className={cn(
            'mt-1 text-[11px] font-medium uppercase tracking-wide',
            online ? 'text-muted-foreground/70' : 'text-amber-600',
          )}
        >
          {stale}
        </p>
      )}
    </div>
  );
}

function EmptyState({ text, icon }: { text: string; icon?: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 rounded-xl border border-dashed border-border bg-card/40 p-4 text-sm text-muted-foreground">
      {icon ?? <InboxIcon className="h-4 w-4 text-muted-foreground/60" aria-hidden="true" />}
      {text}
    </div>
  );
}
